import { useEffect, useMemo, useRef, useState } from "react"

import { cn } from "./cn"

export interface RadialDottedProgressProps {
  /** 0..100 — share of ticks drawn in the filled color, clockwise from the top. */
  progress: number
  /** Tick length, pointing outward, in px. Default 16. */
  lineLength?: number
  /** Gap between ticks along the perimeter, in px. Default 3. */
  spacing?: number
  /** Tick thickness, in px. Default 1. */
  strokeWidth?: number
  /** Gap between the traced edge and the start of each tick, in px. Default 8. */
  offset?: number
  /**
   * Corner radius of the traced rounded rectangle, in px. Clamped to half the
   * shorter side, so the default traces a stadium — fully-rounded caps joined by
   * straight sides, the shape the design uses.
   */
  cornerRadius?: number
  /** Color of filled ticks. Default `currentColor`. */
  filledColor?: string
  /** Color of not-yet-filled ticks. */
  emptyColor?: string
  className?: string
}

interface Tick {
  x1: number
  y1: number
  x2: number
  y2: number
}

interface Segment {
  len: number
  /** Point and outward normal at distance `d` along this segment. */
  at: (d: number) => { x: number; y: number; nx: number; ny: number }
}

/**
 * A dotted/ticked progress ring that traces the **rounded rectangle** inscribed
 * in its own box — with the default corner radius, a stadium: semicircular caps
 * joined by straight sides. Ticks are spread evenly by arc length (not by angle,
 * which would bunch them at the caps) and point along the outward normal.
 * `progress` fills them clockwise from the top.
 *
 * Self-contained: it measures itself, so drop it into a `position: relative`
 * parent as an absolutely-positioned overlay (it fills the parent via `inset-0`).
 */
export function RadialDottedProgress({
  progress,
  lineLength = 16,
  spacing = 3,
  strokeWidth = 1,
  offset = 8,
  cornerRadius = 999,
  filledColor = "currentColor",
  emptyColor = "rgba(255, 255, 255, 0.2)",
  className,
}: RadialDottedProgressProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const { w, h } = size

  // Geometry depends only on the box and tick metrics — not on progress — so it
  // is recomputed only on resize, while progress just recolors existing ticks.
  const ticks = useMemo(
    () =>
      w > 0 && h > 0
        ? computeRoundedRectTicks(w, h, cornerRadius, spacing, offset, lineLength)
        : [],
    [w, h, cornerRadius, spacing, offset, lineLength],
  )

  const filledCount = Math.round((clamp(progress) / 100) * ticks.length)

  return (
    <div ref={ref} className={cn("pointer-events-none absolute inset-0", className)}>
      {ticks.length > 0 ? (
        <svg width={w} height={h} className="absolute inset-0" style={{ overflow: "visible" }}>
          {ticks.map((t, i) => (
            <line
              key={i}
              x1={t.x1}
              y1={t.y1}
              x2={t.x2}
              y2={t.y2}
              stroke={i < filledCount ? filledColor : emptyColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              style={{ transition: "stroke 0.3s ease" }}
            />
          ))}
        </svg>
      ) : null}
    </div>
  )
}

/**
 * Evenly-by-arc-length tick positions around the rounded rectangle inscribed in
 * `w × h`, starting at the top centre and going clockwise. Each tick starts
 * `offset` outside the edge and extends `lineLength` further along the normal.
 */
function computeRoundedRectTicks(
  w: number,
  h: number,
  cornerRadius: number,
  spacing: number,
  offset: number,
  lineLength: number,
): Tick[] {
  const r = Math.max(0, Math.min(cornerRadius, Math.min(w, h) / 2))
  const topHalf = Math.max(0, w / 2 - r) // the top edge is split by the start point
  const side = Math.max(0, h - 2 * r)
  const bottom = Math.max(0, w - 2 * r)
  const arc = (Math.PI / 2) * r

  /** A quarter arc centred at (cx, cy), sweeping from `from` radians over +π/2. */
  const quarter = (cx: number, cy: number, from: number): Segment => ({
    len: arc,
    at: (d) => {
      const angle = from + (r > 0 ? d / r : 0)
      const nx = Math.cos(angle)
      const ny = Math.sin(angle)
      return { x: cx + r * nx, y: cy + r * ny, nx, ny }
    },
  })

  // Clockwise from the top centre. With a stadium (r = w/2) the straight top and
  // bottom runs collapse to zero length and the ring starts at the very top.
  const segments: Segment[] = [
    { len: topHalf, at: (d) => ({ x: w / 2 + d, y: 0, nx: 0, ny: -1 }) },
    quarter(w - r, r, -Math.PI / 2),
    { len: side, at: (d) => ({ x: w, y: r + d, nx: 1, ny: 0 }) },
    quarter(w - r, h - r, 0),
    { len: bottom, at: (d) => ({ x: w - r - d, y: h, nx: 0, ny: 1 }) },
    quarter(r, h - r, Math.PI / 2),
    { len: side, at: (d) => ({ x: 0, y: h - r - d, nx: -1, ny: 0 }) },
    quarter(r, r, Math.PI),
    { len: topHalf, at: (d) => ({ x: r + d, y: 0, nx: 0, ny: -1 }) },
  ]

  const perimeter = segments.reduce((sum, s) => sum + s.len, 0)
  if (perimeter <= 0) return []

  const count = Math.max(1, Math.round(perimeter / spacing))
  const ticks: Tick[] = []
  for (let i = 0; i < count; i++) {
    let d = (i / count) * perimeter
    for (const seg of segments) {
      if (d > seg.len) {
        d -= seg.len
        continue
      }
      const { x, y, nx, ny } = seg.at(d)
      ticks.push({
        x1: x + nx * offset,
        y1: y + ny * offset,
        x2: x + nx * (offset + lineLength),
        y2: y + ny * (offset + lineLength),
      })
      break
    }
  }
  return ticks
}

function clamp(value: number) {
  return Math.min(Math.max(value, 0), 100)
}
